import { useEffect, useState } from "react";
import { useRecoilValue } from "recoil";
import { Capacitor } from "@capacitor/core";
import { Box, Button, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SystemUpdateOutlinedIcon from "@mui/icons-material/SystemUpdateOutlined";
import { ortexColorState, pushPrePromptState } from "@/lib/store";
import { isAppVersionAtLeast, isPrePromptBlockedPath } from "@/lib/push";
import { isOrtexApp } from "@/lib/utils";
import { getAppVersion } from "src/hooks/usePushNotifications";

// The newest store build. The app shell loads this web code from app.ortex.com, so every
// installed build, old or new, runs this check. Set NEXT_PUBLIC_LATEST_APP_VERSION (Vercel)
// when a release reaches both stores, or bump the default here.
export const LATEST_APP_VERSION = process.env.NEXT_PUBLIC_LATEST_APP_VERSION || "1.0.16";

// Store pages, opened in the store apps themselves: Capacitor hands non-http schemes to the OS.
const APP_STORE_URL = "itms-apps://apps.apple.com/app/id6443720192";
const PLAY_STORE_URL = "market://details?id=com.ortex.application";

// Not annoying: it waits a few seconds after the app is ready, never covers sign-in or the
// notifications pre-prompt, and "x" hides it for a week (per version, so a newer release shows again).
const SHOW_DELAY_MS = 6000;
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
const snoozeKey = (latest: string) => `ortex_update_banner_snoozed_at:${latest}`;

const isSnoozed = (latest: string) => {
    try {
        const at = Number(localStorage.getItem(snoozeKey(latest)));
        return !!at && Date.now() - at < SNOOZE_MS;
    } catch {
        return false;
    }
};

const snooze = (latest: string) => {
    try {
        localStorage.setItem(snoozeKey(latest), String(Date.now()));
    } catch {
        // storage unavailable: it may show again next launch
    }
};

interface Props {
    pathname: string;
    // The app has finished loading the user (signed in or not).
    ready: boolean;
}

/**
 * "A new version of ORTEX is available": a small dismissible card at the bottom of the
 * screen, in the iOS and Android apps only, when the installed build is older than
 * LATEST_APP_VERSION. Update opens the App Store or Play Store page.
 */
const AppUpdateBanner = ({ pathname, ready }: Props) => {
    const ortexColor = useRecoilValue(ortexColorState);
    const prePrompt = useRecoilValue(pushPrePromptState);
    const [outdated, setOutdated] = useState(false);
    const [visible, setVisible] = useState(false);
    const platform = Capacitor.getPlatform();
    const native = isOrtexApp() && (platform === "ios" || platform === "android");

    useEffect(() => {
        if (!native || isSnoozed(LATEST_APP_VERSION)) return;
        let cancelled = false;
        getAppVersion().then((version) => {
            if (!cancelled && version && !isAppVersionAtLeast(version, LATEST_APP_VERSION)) setOutdated(true);
        });
        return () => {
            cancelled = true;
        };
    }, [native]);

    useEffect(() => {
        if (!outdated || !ready || visible) return;
        const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
        return () => clearTimeout(t);
    }, [outdated, ready, visible]);

    if (!visible || isPrePromptBlockedPath(pathname) || prePrompt.open) return null;

    const dismiss = () => {
        snooze(LATEST_APP_VERSION);
        setVisible(false);
    };

    const update = () => {
        snooze(LATEST_APP_VERSION);
        setVisible(false);
        window.location.href = platform === "ios" ? APP_STORE_URL : PLAY_STORE_URL;
    };

    return (
        <Box
            role="status"
            sx={{
                position: "fixed",
                left: 12,
                right: 12,
                bottom: "calc(12px + env(safe-area-inset-bottom))",
                zIndex: 1300,
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                borderRadius: "10px",
                border: "1px solid rgba(255,255,255,0.12)",
                backgroundColor: "rgba(20,22,24,0.96)",
                color: "#fff",
                boxShadow: "0 6px 24px rgba(0,0,0,0.35)",
                maxWidth: 520,
                mx: "auto",
            }}
        >
            <SystemUpdateOutlinedIcon fontSize="small" sx={{ color: ortexColor, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ flex: 1, fontSize: 13, lineHeight: 1.35 }}>
                A new version of the ORTEX app is available.
            </Typography>
            <Button size="small" variant="contained" onClick={update} sx={{ backgroundColor: ortexColor, flexShrink: 0, "&:hover": { backgroundColor: ortexColor } }}>
                Update
            </Button>
            <IconButton size="small" aria-label="Not now" onClick={dismiss} sx={{ color: "rgba(255,255,255,0.6)", flexShrink: 0 }}>
                <CloseIcon fontSize="small" />
            </IconButton>
        </Box>
    );
};

export default AppUpdateBanner;
